import { createContext, useContext, useState, type ReactNode } from "react";

const MAX_OPEN_FLOATING_CHATS = 3;

interface FloatingChatsContextValue {
  openChatIds: string[];
  openFloatingChat: (conversationId: string) => void;
  closeFloatingChat: (conversationId: string) => void;
}

const FloatingChatsContext = createContext<FloatingChatsContextValue | null>(null);

export function FloatingChatsProvider({ children }: { children: ReactNode }) {
  const [openChatIds, setOpenChatIds] = useState<string[]>([]);

  function openFloatingChat(conversationId: string) {
    setOpenChatIds((prev) => {
      if (prev.includes(conversationId)) return prev;
      // Drop the oldest one rather than silently refusing to open a new
      // chat - keeps the cap meaningful (avoid the screen filling with
      // windows) without the action just doing nothing from the person's
      // point of view.
      const next = [...prev, conversationId];
      return next.length > MAX_OPEN_FLOATING_CHATS ? next.slice(1) : next;
    });
  }

  function closeFloatingChat(conversationId: string) {
    setOpenChatIds((prev) => prev.filter((id) => id !== conversationId));
  }

  return (
    <FloatingChatsContext.Provider value={{ openChatIds, openFloatingChat, closeFloatingChat }}>
      {children}
    </FloatingChatsContext.Provider>
  );
}

export function useFloatingChats() {
  const ctx = useContext(FloatingChatsContext);
  if (!ctx) throw new Error("useFloatingChats must be used within FloatingChatsProvider");
  return ctx;
}
